import React, {
  useState, useEffect, Dispatch, SetStateAction,
} from 'react'
import {
  ListItem,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material'
import { useDispatch } from '@app/store'
import { regexpChecker } from '@app/helpers/regexpChecker'
import { requiredFields, validation } from '@app/lib/ProgramOptions/validation'
import { ClearIcon } from 'icons'
import useStyles from '@app/components/ProgramOptions/ProgramOption/InputField/InputField.style'
import { IProgramOptions } from '@app/@types/programOptions'
import { InferValueTypes } from '@app/@types/typesHelper'
import * as programOptionsActions from '@app/actions/programOptions'

type Props = {
  number: number,
  value: string,
  resetInputField: () => void,
  options: IProgramOptions,
  setOptions: Dispatch<SetStateAction<IProgramOptions>>,
  name: string,
  validationChecker: (value: string, min: number, max: number) => boolean,
  validationIntervals: Record<string, { min: number, max: number, required?: boolean }>,
  validationSetter: (validation: boolean) => ReturnType<InferValueTypes<
    typeof programOptionsActions>>,
  inProgress: boolean,
}

const InputField: React.FC<Props> = (props) => {
  const classes: any = useStyles()
  const dispatch = useDispatch()
  const [currentValue, setCurrentValue] = useState<string>('')
  const [fieldIsNotValid, setFieldIsNotValid] = useState<boolean>(false)
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const {
    number, value, options, setOptions, name, resetInputField, validationChecker, validationIntervals, validationSetter, inProgress,
  } = props

  useEffect(() => {
    setCurrentValue(value)
  }, [])

  useEffect(() => {
    // @ts-ignore -- TS5: index access on typed object (was suppressed by suppressImplicitAnyIndexErrors)
    setCurrentValue(value)
  }, [value])

  useEffect(() => {
    if (currentValue === '' && requiredFields.includes(number)) {
      setFieldIsNotValid(true)
      setIsRequired(true)
    } else {
      setIsRequired(false)
      isFieldNotValid()
    }
  }, [currentValue])

  useEffect(() => {
    dispatch(validationSetter(!fieldIsNotValid))
  }, [fieldIsNotValid])

  const setInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions(options.map((rule) => (rule.number === number
      ? { ...rule, value: event.target.value.replace(/\s+/g, '') } : rule)))
    setCurrentValue(event.target.value.replace(/\s+/g, ''))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (validationIntervals[`${number}`]) {
      if (!Number.isNaN(+event.target.value)) {
        setInputValue(event)
      }
    } else {
      setInputValue(event)
    }
  }

  const isFieldNotValid = () => {
    if (validationIntervals[`${number}`]) {
      setFieldIsNotValid(!validationChecker(currentValue, validationIntervals[`${number}`].min, validationIntervals[`${number}`].max))
    } else if (currentValue) {
      if (Object.keys(validation).includes(number.toString())) {
        // @ts-ignore TS5: index expression on typed object
        setFieldIsNotValid(!regexpChecker(value, validation[`${number}`]))
      }
    }
  }

  const createAdorment = () => (
    <InputAdornment position="start">
      <IconButton className={classes.resetFieldButton} onClick={resetInputField} aria-label="Clear">
        <ClearIcon className={classes.icon} />
      </IconButton>
    </InputAdornment>
  )

  const createTextField = () => (
    <TextField
      label={I18n.t(`program_options.po_${number}`)}
      variant="outlined"
      onChange={handleChange}
      value={currentValue}
      InputProps={{
        endAdornment: createAdorment(),
        name,
      }}
      error={!inProgress && fieldIsNotValid}
    />
  )

  return (
    <ListItem className={classes.root} key={name}>
      <FormControl variant="standard" className={classes.input}>
        {createTextField()}
        <FormHelperText className={classes.text}>{I18n.t(`hint.po_${number}_hint`)}</FormHelperText>
        {isRequired && (
          <FormHelperText className={classes.required}>{I18n.t('validations.name_required')}</FormHelperText>
        )}
      </FormControl>
    </ListItem>
  )
}

export default InputField
